'use client';

import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { ShopperSearch } from '@/scapi';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerTitle } from '@/components/ui/drawer';
import EatalyPlpOrdinationOptions from '@/components/eataly-plp-ordination/plp-ordination-options';
import { usePlpOrdinationSelect } from '@/components/eataly-plp-ordination/use-plp-ordination-select';
import { useMediaQuery } from '@/hooks/use-media-query';
import { cn } from '@/lib/utils';

const LG_UP = '(min-width: 1024px)';

type PlpOrdinationDrawerResult = {
    total?: number;
    sortingOptions?: ShopperSearch.schemas['ProductSearchResult']['sortingOptions'];
};

export interface EatalyCategoryPlpOrdinationDrawerProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    result: PlpOrdinationDrawerResult;
    sortingOptionIds?: string[];
    /** Sort selected when the URL has no `sort` param. News listings use newest-first. */
    defaultSortId?: string;
}

export default function EatalyCategoryPlpOrdinationDrawer({
    open,
    onOpenChange,
    result,
    sortingOptionIds,
    defaultSortId,
}: EatalyCategoryPlpOrdinationDrawerProps): ReactElement | null {
    const { t } = useTranslation(['categoryPlp']);
    const isLgUp = useMediaQuery(LG_UP);
    const { isPending, effectiveSortingOption, sortingOptions, selectSort } = usePlpOrdinationSelect(result, {
        sortingOptionIds,
        defaultSortId,
    });

    const ordinationTitle = t('categoryPlp:ordinationDrawer.title');

    if (sortingOptions.length === 0 || isLgUp) {
        return null;
    }

    return (
        <Drawer direction="bottom" open={open} onOpenChange={onOpenChange} shouldScaleBackground={false}>
            <DrawerContent
                className={cn(
                    'bg-neutral-white',
                    'data-[vaul-drawer-direction=bottom]:max-h-[90vh]',
                    'data-[vaul-drawer-direction=right]:mt-0 data-[vaul-drawer-direction=right]:max-h-dvh data-[vaul-drawer-direction=right]:h-dvh',
                    'data-[vaul-drawer-direction=right]:w-full data-[vaul-drawer-direction=right]:max-w-[393px] data-[vaul-drawer-direction=right]:sm:max-w-[393px]'
                )}
                showDivider={false}>
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                    <div className="flex shrink-0 items-center justify-between p-6">
                        <DrawerTitle className="heading-title-m">{ordinationTitle}</DrawerTitle>
                        <DrawerClose asChild>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="size-10 shrink-0 rounded-full border border-transparent bg-neutral-white p-2 hover:border-neutral-80"
                                aria-label={t('categoryPlp:ordinationDrawer.closeAriaLabel')}>
                                <X className="size-6 text-neutral-10" aria-hidden />
                            </Button>
                        </DrawerClose>
                    </div>

                    <DrawerDescription className="sr-only">
                        {t('categoryPlp:ordinationDrawer.description')}
                    </DrawerDescription>

                    <div className="min-h-0 flex-1 overflow-y-auto px-6 mb-10 pt-6">
                        <EatalyPlpOrdinationOptions
                            sortingOptions={sortingOptions}
                            effectiveSortingOption={effectiveSortingOption}
                            isPending={isPending}
                            onSelectSort={selectSort}
                            ariaLabel={ordinationTitle}
                            idPrefix="ordination-drawer"
                        />
                    </div>
                </div>
            </DrawerContent>
        </Drawer>
    );
}
